import { type CustomFieldChangeset } from "@/shared/domain/customFields";

/**
 * Every member type a collection can hold, in display order.
 *
 * The array is the source of truth and {@link CollectionResourceType} derives
 * from it, rather than the other way round, so anywhere that needs to enumerate
 * the types at runtime -- a filter's options, a tool's schema enum, a sentence
 * listing them -- reads this instead of a hand-written copy the compiler cannot
 * check. Widening a hand-written copy's neighbours is how three of them went
 * stale at once.
 */
export const COLLECTION_RESOURCE_TYPES = [
  "file",
  "dataset",
  "event",
  "session",
] as const;

export type CollectionResourceType = (typeof COLLECTION_RESOURCE_TYPES)[number];

export interface CollectionRecord {
  collection_id: string;
  name: string | null;
  description: string | null;
  resource_type: CollectionResourceType;
  resources: { [key: string]: unknown[] };
  missing: { [key: string]: unknown[] };
  /**
   * Number of resources the collection holds. Maintained server-side as
   * members change, so it is present in every content mode, including the
   * summary-only rows the collections list reads where `resources` is empty.
   */
  resource_count: number;
  tags: string[];
  custom_fields: Record<string, unknown>;
  version: number;
  created: string;
  created_by: string;
  updated: string;
  updated_by: string;
  org_id: string;
}

export interface CollectionResource {
  resource_type: CollectionResourceType;
  resource_id: string;
  resource_version: string;
}

export interface CollectionChangeSet {
  added_resources: CollectionResource[];
  removed_resources: CollectionResource[];
  removed_tags: string[];
  added_tags: string[];
  field_changes: { [key: string]: unknown[] };
}

export interface CollectionChangeRecord {
  collection_id: string;
  from_version: number;
  to_version: number;
  change_set: CollectionChangeSet;
  applied: string;
  applied_by: string;
}

export interface CollectionResourceRef {
  resource_type: CollectionResourceType;
  resource_id: string;
  resource_version?: string;
}

export interface CreateCollectionRequest {
  name: string;
  description?: string;
  tags?: string[];
  resource_type: CollectionResourceType;
  resources?: CollectionResourceRef[];
  /** Initial values for Ready custom fields on the new collection, by field name. */
  custom_fields?: Record<string, unknown>;
}

export interface UpdateCollectionRequest {
  name?: string;
  description?: string;
  add_tags?: string[];
  remove_tags?: string[];
  add_resources?: CollectionResourceRef[];
  remove_resources?: CollectionResourceRef[];
  /** Set/clear Ready custom-field values; absent = no change. */
  custom_fields_changeset?: CustomFieldChangeset;
}
